/**
 * @schulspiele/protocol
 *
 * The wire protocol between server and clients. This package holds types
 * and constants only: no runtime logic, no dependencies. Both the server
 * and the client import from here, so a message shape can never drift
 * between the two sides.
 *
 * The full message catalogue with examples is in docs/PROTOCOL.md. When you
 * add a message there, add its type here in the same commit.
 */
/** Protocol version. Bump on any incompatible change; the hello handshake checks it. */
export const PROTOCOL_VERSION = 1;
/** Error codes the server may send in an `error` envelope. */
export const ERROR_CODES = {
    /** The frame was not valid JSON or not an envelope. The connection is closed. */
    malformed: 'malformed',
    /** Unknown `type`. */
    unknownType: 'unknown_type',
    /** The client's protocol version does not match {@link PROTOCOL_VERSION}. */
    versionMismatch: 'version_mismatch',
    /** Too many messages; the connection is closed. */
    rateLimited: 'rate_limited',
    /** The sender's role may not send this message. */
    forbidden: 'forbidden',
    /** No session with this id, or it has ended. */
    noSuchSession: 'no_such_session',
    /** The module rejected an intent in the current state. `reason` holds the German text. */
    intentRejected: 'intent_rejected',
    /** The core refused a session request (bad name, locked lobby, wrong phase); `reason` says why in German. */
    rejected: 'rejected',
};
/** All connection-level message types, as a lookup for both sides. */
export const CONNECTION_MESSAGES = {
    hello: 'hello',
    error: 'error',
    /**
     * Server → client: the request with this `ref` succeeded. Sent for every
     * request that carries a `ref` and has no reply of its own (attach,
     * intents, teacher commands). Lets the client resolve its promise exactly
     * when the server has done the work, instead of guessing with a timeout.
     */
    ack: 'ack',
};
/* ------------------------------------------------------------------------ */
/* Session messages (all carry sessionId)                                    */
/* ------------------------------------------------------------------------ */
export const SESSION_MESSAGES = {
    /** client → server (teacher, board): attach this socket to a session. */
    attach: 'session/attach',
    /** client → server (student): join with pin + name, or reconnect with a token. */
    join: 'session/join',
    /** server → client (student): reply to join. */
    joined: 'session/joined',
    /** server → client (all): phase, lock, participants (not for students). */
    state: 'session/state',
    kick: 'session/kick',
    /** client → server (teacher): remove every child whose phone is away ("Alle grauen entfernen", U19). */
    kickOffline: 'session/kickOffline',
    /** client → server (student): give up the seat ("Anderes Spiel?"); the server frees name and place. */
    leave: 'session/leave',
    rename: 'session/rename',
    lock: 'session/lock',
    start: 'session/start',
    end: 'session/end',
    /** server → client (student): then the socket is closed. */
    kicked: 'session/kicked',
    ping: 'session/ping',
    pong: 'session/pong',
};
/* ------------------------------------------------------------------------ */
/* Game messages                                                             */
/* ------------------------------------------------------------------------ */
export const GAME_MESSAGES = {
    /** client → server: a module intent, wrapped. */
    intent: 'game/intent',
    /** server → client: this connection's view after every change. */
    view: 'game/view',
    /** server → client: one-off event from the module (sound cue etc.). */
    event: 'game/event',
};
/** Upload limits per kind, in bytes. The server enforces them; the client shows them. */
export const MEDIA_LIMITS = {
    image: 10 * 1024 * 1024,
    audio: 20 * 1024 * 1024,
    video: 200 * 1024 * 1024,
};
/* ------------------------------------------------------------------------ */
/* Teams                                                                     */
/* ------------------------------------------------------------------------ */
/**
 * Team colours, one per design token `--team-<color>` in tokens.css: forest
 * animals. Shared so every format with teams (Jeopardy, Buzzer, …) offers
 * the same six and the start dialog's `teams` field can show them.
 */
export const TEAM_COLORS = ['fox', 'owl', 'deer', 'frog', 'bee', 'hedgehog'];
/** Each colour with its German animal (accessible name) and a default team name. */
export const TEAM_PRESETS = [
    { color: 'fox', label: 'Fuchs', name: 'Füchse' },
    { color: 'owl', label: 'Eule', name: 'Eulen' },
    { color: 'deer', label: 'Reh', name: 'Rehe' },
    { color: 'frog', label: 'Frosch', name: 'Frösche' },
    { color: 'bee', label: 'Biene', name: 'Bienen' },
    { color: 'hedgehog', label: 'Igel', name: 'Igel' },
];
/* ------------------------------------------------------------------------ */
/* Question bank (HTTP, ADR 0015)                                            */
/* ------------------------------------------------------------------------ */
/** Levels: classes 5 to 10 and adults. Every class is a level of its own. */
export const BANK_LEVELS = ['5', '6', '7', '8', '9', '10', 'erw'];
export const BANK_LIMITS = {
    /** Games show at most 300; longer questions are kept but stay drafts. */
    maxText: 500,
    playableText: 300,
    maxExplanation: 300,
    maxTags: 10,
    maxTag: 30,
    maxSubjectName: 40,
    maxTopicName: 80,
    minDifficulty: 1,
    maxDifficulty: 10,
    /** Page size of the list. */
    pageSize: 50,
    maxPageSize: 200,
    /** Questions a random draw may return. */
    maxDraw: 100,
    /**
     * Questions a format that needs many may draw for one session
     * (`manifest.bank.draw`, ADR 0018), beyond what a teacher picks.
     */
    maxGameDraw: 300,
    /** Items in one import. */
    maxImport: 60_000,
};
/** Whether `v` is a drawing as the app draws it (sets carry them; checked on save). */
export function isFigureRef(v) {
    if (typeof v !== 'object' || v === null)
        return false;
    const r = v;
    if (r.at !== 'question' && r.at !== 'reveal')
        return false;
    const f = r.figure;
    if (typeof f !== 'object' || f === null)
        return false;
    const num = (x, lo, hi) => typeof x === 'number' && Number.isFinite(x) && x >= lo && x <= hi;
    const int = (x, lo, hi) => num(x, lo, hi) && Number.isInteger(x);
    switch (f.kind) {
        case 'numberline':
            return (num(f.min, -1e9, 1e9) &&
                num(f.max, -1e9, 1e9) &&
                f.min < f.max &&
                Array.isArray(f.points) &&
                f.points.length <= 6 &&
                f.points.every((p) => num(p, f.min, f.max)));
        case 'fraction':
            return (int(f.num, 0, 100) &&
                int(f.den, 1, 100) &&
                (f.num2 === undefined || int(f.num2, 0, 100)) &&
                (f.den2 === undefined || int(f.den2, 1, 100)) &&
                (f.num2 === undefined) === (f.den2 === undefined));
        case 'clock':
            return int(f.h, 0, 23) && int(f.m, 0, 59);
        case 'angle':
            return num(f.deg, 0, 360);
        case 'rect':
            return (num(f.a, 0.01, 10000) &&
                num(f.b, 0.01, 10000) &&
                typeof f.unit === 'string' &&
                f.unit.length <= 12);
        default:
            return false;
    }
}
/* ---- Quiz-Generator (U13, ADR 0020) -------------------------------------------------------- */
/** The five difficulty steps of the generator, easiest first. */
export const GEN_STEPS = ['Sehr leicht', 'Leicht', 'Mittel', 'Schwer', 'Sehr schwer'];
export const GEN_LIMITS = {
    /** Questions of other levels: at most this many levels above or below. */
    levelReach: 2,
    /** One level is worth this many points of difficulty (1 to 10). */
    pointsPerLevel: 2,
    /** A question without a difficulty counts as this. */
    unknownDifficulty: 5,
    /** One task type fills at most this share of a quiz, while others are there (U16). */
    maxTypeShare: 0.4,
    maxTopic: 80,
    maxCount: 300,
    minColumns: 3,
    maxColumns: 6,
    minRows: 3,
    maxRows: 6,
    maxTitle: 200,
};
//# sourceMappingURL=index.js.map